import React, { useEffect, useRef } from "react";
import "./phone.css";
import { ASSETS, CALENDLY_URL, PRODUCTION_URL } from "../config";
import { usePageSeo, absoluteUrl, ORGANISATION_JSON_LD } from "../useSeo";
import { Header, BookACallLink } from "../Header";
import { FloatingCta, Footer } from "../FooterAndFloating";
import { EnquiryForm } from "../EnquiryForm";

const DESCRIPTION =
  "How to Turn Your Phone Off is a stage play designed to change your relationship with your smartphone and give your team a memorable, genuinely useful experience.";

const PAGE = {
  title: "How to Turn Your Phone Off | The Business Playwright",
  description: DESCRIPTION,
  path: "/how-to-turn-your-phone-off",
  ogImage: ASSETS.phonePoster,
  ogImageAlt: "How to Turn Your Phone Off theatre poster",
  jsonLd: {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "How to Turn Your Phone Off",
    url: absoluteUrl("/how-to-turn-your-phone-off"),
    description: DESCRIPTION,
    primaryImageOfPage: absoluteUrl(ASSETS.phonePoster),
    isPartOf: { "@type": "WebSite", name: "The Business Playwright", url: PRODUCTION_URL },
    publisher: ORGANISATION_JSON_LD,
    potentialAction: { "@type": "ReserveAction", target: CALENDLY_URL },
  },
};

export default function HowToTurnYourPhoneOffPage() {
  usePageSeo(PAGE);
  const heroRef = useRef(null);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="bp-site pt-page" data-testid="phone-page">
      <Header />
      <main id="main">
        <section className="pt-hero" ref={heroRef} aria-labelledby="pt-hero-title" data-testid="pt-hero">
          <div className="bp-wrap pt-hero-grid">
            <div className="pt-hero-copy">
              <span className="pt-rule" aria-hidden="true" />
              <h1 id="pt-hero-title" className="pt-title" data-testid="pt-hero-title">HOW TO TURN YOUR PHONE OFF</h1>
              <div className="pt-lead" data-testid="pt-hero-lead">
                <p>Everyone knows they use their phone too much.</p>
                <p>Everyone knows it's leaving them drained, frustrated, and time-poor.</p>
                <p className="pt-lead-emphasis" data-testid="pt-hero-emphasis">Nobody needs another presentation about it.</p>
              </div>
              <div className="pt-body">
                <p>How to Turn Your Phone Off is a stage play that will be seared in your memory long after you have finished watching it.</p>
                <p>It's not about tips and tactics (though it does include a few of those). It's about causing a mindset shift that will irrevocably change your relationship with your smartphone.</p>
              </div>
              <BookACallLink label="BOOK NOW" testId="pt-hero-cta" />
            </div>
            <div className="pt-hero-poster">
              <img src={ASSETS.phonePoster} alt="How to Turn Your Phone Off theatre poster" width="1024" height="1536" fetchPriority="high" decoding="async" data-testid="pt-hero-poster" />
            </div>
          </div>
        </section>

        <section className="bp-scene pt-scene" aria-label="Why this play stays with people" data-testid="pt-body">
          <div className="bp-wrap pt-column">
            <div className="pt-body">
              <p>Rory Sutherland describes stories as PDFs for the mind. This one will remain on your mind's desktop for years.</p>
              <p>Every time you're about to get caught in mindless scrolling, the memory of this play will give you the chance to step back from the brink.</p>
            </div>
            <p className="pt-statement" data-testid="pt-statement">In under 90 minutes you'll regain control of your future.</p>
          </div>
        </section>

        <section className="bp-scene pt-scene pt-final" aria-labelledby="pt-final-title" data-testid="pt-final">
          <div className="bp-wrap pt-final-grid">
            <div className="pt-column">
              <h2 id="pt-final-title" className="pt-final-title" data-testid="pt-final-title">This might be the greatest gift you can ever give your colleagues.</h2>
              <BookACallLink label="BOOK A 30-MINUTE CALL" testId="pt-final-cta" />
            </div>
            <EnquiryForm production="how-to-turn-your-phone-off" prefix="pt-enquiry" />
          </div>
        </section>
      </main>
      <Footer />
      <FloatingCta heroRef={heroRef} label="BOOK NOW" testId="pt-floating-cta" />
    </div>
  );
}
